import { Link, NavLink, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { LanguageSwitcher } from '@/components/language-switcher';
import { ClientTour } from '@/components/client-tour';
import { useClientStep } from '@/lib/use-client-step';
import { usePostStepTransitionRedirect } from '@/lib/use-post-step-transition';
import { useToolRouteEffects } from '@/lib/use-tool-route-effects';
import { useAuth } from './auth-context';

export function AppLayout(): JSX.Element {
  const { t } = useTranslation();
  const { user, signOut } = useAuth();
  // NL-001 — at Step 0 the client web exposes only Dashboard, Messages,
  // and Settings in the nav. Daily Check-in (Step 1) and Carpe Diem
  // (Step 2) hide until the client advances. Coaches (isClient=false)
  // see every link unconditionally; clients with a failed step fetch
  // default conservatively to hidden.
  const { step: clientStep, isClient } = useClientStep();
  const showCheckInNav = !isClient || (clientStep ?? 0) >= 1;
  const showCarpeDiemNav = !isClient || (clientStep ?? 0) >= 2;
  // NL-006 / NL-010 / NL-014 / NL-018 — first-login post-step redirect.
  // No-ops when the user is not a client, when step hasn't changed,
  // or when they're already on the destination URL.
  usePostStepTransitionRedirect();
  // NL-024 + NL-027 — finalized-tool → read-only /view redirect, and
  // `?coachEdit` token plumbing for coach edit-mode saves.
  useToolRouteEffects();

  return (
    <div className="min-h-screen flex flex-col">
      <header className="bg-bone border-b border-bone-3">
        <div className="max-w-6xl mx-auto px-s5 py-s4 flex items-center justify-between gap-s4">
          <Link
            to="/"
            className="font-display text-2xl text-ink hover:text-flame-deep transition-colors flex-shrink-0"
          >
            {t('common.appName')}
          </Link>

          {/* F-030 follow-up — horizontally scrollable nav on mobile so
              every menu item is reachable without burying the dashboard
              entry under a hamburger. `overflow-x-auto` + flex-nowrap
              gives the swipe affordance; `flex-shrink-0` on each link
              prevents the labels from compressing into ellipses. */}
          <nav className="flex items-center gap-s4 overflow-x-auto whitespace-nowrap flex-1 min-w-0">
            {user && (
              <>
                <NavLink
                  to="/dashboard"
                  className={({ isActive }) =>
                    `flex-shrink-0 ${
                      isActive
                        ? 'text-flame-deep font-medium'
                        : 'text-gray-2 hover:text-ink transition-colors'
                    }`
                  }
                >
                  {t('dashboard.title')}
                </NavLink>
                {user.role === 'client' && showCheckInNav && (
                  <NavLink
                    to="/check-in"
                    data-testid="nav-check-in"
                    className={({ isActive }) =>
                      `flex-shrink-0 ${
                        isActive
                          ? 'text-flame-deep font-medium'
                          : 'text-gray-2 hover:text-ink transition-colors'
                      }`
                    }
                  >
                    {t('checkIn.title')}
                  </NavLink>
                )}
                {user.role === 'client' && showCarpeDiemNav && (
                  <NavLink
                    to="/carpe-diem"
                    data-testid="nav-carpe-diem"
                    className={({ isActive }) =>
                      `flex-shrink-0 ${
                        isActive
                          ? 'text-flame-deep font-medium'
                          : 'text-gray-2 hover:text-ink transition-colors'
                      }`
                    }
                  >
                    {t('carpeDiem.nav')}
                  </NavLink>
                )}
                {user.role === 'client' && (
                  <NavLink
                    to="/messages"
                    className={({ isActive }) =>
                      `flex-shrink-0 ${
                        isActive
                          ? 'text-flame-deep font-medium'
                          : 'text-gray-2 hover:text-ink transition-colors'
                      }`
                    }
                  >
                    {t('messages.title')}
                  </NavLink>
                )}
                {(user.role === 'head_coach' || user.role === 'coach') && (
                  <NavLink
                    to="/clients"
                    className={({ isActive }) =>
                      `flex-shrink-0 ${
                        isActive
                          ? 'text-flame-deep font-medium'
                          : 'text-gray-2 hover:text-ink transition-colors'
                      }`
                    }
                  >
                    {t('clients.title')}
                  </NavLink>
                )}
                <NavLink
                  to="/settings"
                  className={({ isActive }) =>
                    `flex-shrink-0 ${
                      isActive
                        ? 'text-flame-deep font-medium'
                        : 'text-gray-2 hover:text-ink transition-colors'
                    }`
                  }
                >
                  {t('settings.title')}
                </NavLink>
              </>
            )}
          </nav>

          <div className="flex items-center gap-s3 flex-shrink-0">
            <LanguageSwitcher />

            {user && (
              <button
                type="button"
                onClick={() => {
                  void signOut();
                }}
                className="btn-ghost text-sm"
              >
                {t('common.actions.logout')}
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="border-t border-bone-3 mt-s8">
        <div className="max-w-6xl mx-auto px-s5 py-s4 text-sm text-gray-2 flex justify-between">
          <span>{t('common.tagline')}</span>
          {/* IMP-002 — version baked from the root package.json at build
              time; keep that version in lockstep with the prod APP_VERSION
              env so this always matches /api/v1/health. */}
          <span className="font-mono text-xs">v{__APP_VERSION__}</span>
        </div>
      </footer>

      {/* WEB-156 — first-run client tour. Renders only for the
          `client` role; the macOS coach console has its own tour
          (MAC-157) so head_coach + coach skip this. */}
      {user?.role === 'client' && <ClientTour userId={user.id} />}
    </div>
  );
}
